import { useState } from "react";
import { COLORS, MONO_FONT } from "../styles/theme";
import { checkPassword } from "../engine/passwordValidation";
import { isValidEmail } from "../engine/emailValidation";
import PasswordRequirements from "./PasswordRequirements";
import * as api from "../engine/api";
import useResendCooldown from "../hooks/useResendCooldown";

// OMEGA-814 — the first field is the customer's FULL NAME (a display name), not a
// login handle. The backend bounds it 1..120 to match `users.full_name`; mirror the
// lower bound here so the form catches an empty one before the round trip. The old
// USERNAME_MIN_LENGTH of 3 is gone with the field it guarded: a display name has no
// minimum beyond being present, and "Bo" is a real name.
const FULL_NAME_MAX_LENGTH = 120;

const inputStyle = {
  width: "100%", padding: "5px 8px", marginTop: 2, borderRadius: 3,
  border: `1px solid ${COLORS.border}`, background: COLORS.panel,
  color: COLORS.text, fontSize: 14, fontFamily: MONO_FONT,
  outline: "none", boxSizing: "border-box",
};

const labelStyle = { fontSize: 12, color: COLORS.textDim, textTransform: "uppercase" };

/**
 * Registration form.
 *
 * OMEGA-762 (2026-08-17) — the card field is GONE. It used to tokenize a card via
 * Stripe Elements and pass the token to ``api.register()``. The platform will not
 * collect payment from a stored card (decision by Rick Zhou; the model is an
 * Omega-issued invoice settled by bank transfer, OMEGA-734 D1), and no code path in
 * the backend could ever have charged one. So this screen asked every customer for
 * a payment instrument that nothing would ever use, which is the false statement
 * the ticket exists to remove.
 */
function RegisterForm({ onRegistered, onBackToLogin }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // E20.4 (OMEGA-987) — the customer's BILLING identity, optional by decision D1. Distinct from
  // Full Name above: that is a display name, this is the entity an invoice is made out to. Left
  // blank the account is created exactly as before and the Account screen prompts for it later;
  // asking here is what lets a customer be invoiced without a second round trip.
  const [legalName, setLegalName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // L01 — verification-pending state: on a no-JWT 201 the account is provisioned
  // but unusable until the emailed magic link is confirmed, so we show a
  // "check your inbox" panel with a resend affordance instead of entering.
  const [pending, setPending] = useState(null);
  const [resendNotice, setResendNotice] = useState("");
  const [resending, setResending] = useState(false);
  // OMEGA-563 — lock the resend button for the backend cooldown window after a
  // successful send so the check-inbox screen stops inviting spam clicks.
  const { remaining, onCooldown, start: startCooldown } = useResendCooldown();

  const submit = async () => {
    setError("");

    if (!fullName.trim() || !password) {
      setError("Full name and password are required");
      return;
    }
    if (fullName.trim().length > FULL_NAME_MAX_LENGTH) {
      setError(`Full name must be ${FULL_NAME_MAX_LENGTH} characters or fewer`);
      return;
    }
    // L01 FR5 — email is required and format-validated client-side (a simple
    // regex; the backend EmailStr is authoritative and hard-rejects the rest).
    if (!isValidEmail(email)) {
      setError("Enter a valid email address");
      return;
    }
    // Complexity gate mirrors the backend policy; the checklist below shows the
    // user exactly which requirement is unmet as they type.
    if (!checkPassword(password).valid) {
      setError("Password does not meet the requirements listed below.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      // L01 FR5/FR7 — api.register returns EITHER a verification-pending body (no
      // JWT — deployed default) OR a TokenResponse (flag off, dev auto-verify — JWT
      // already stored). No payment token is sent: see the component docstring.
      const resp = await api.register(fullName.trim(), email, password, {
        legal_name: legalName,
        // Defaulted to the sign-in address, which is where a sole trader wants invoices anyway.
        // Explicit rather than inferred backend-side: the backend must not guess a billing
        // address from a login identity, and the customer can change it on the Account screen.
        billing_email: billingEmail || email,
      });
      if (resp?.access_token) {
        // Flag off: auto-verified + JWT stored → signed in. The JWT is a registration
        // session (E23.6), so App.jsx shows the card step next, not the Console.
        onRegistered(resp);
        return;
      }
      // Verification pending: show the check-your-inbox state; do NOT enter.
      // OMEGA-814 — the resend affordance below is keyed on the EMAIL, which is the
      // account's login identity. `resp.username` is that same address for any account
      // this screen creates; `email` is the local fallback when the body is unexpected.
      setPending({ identifier: resp?.username || email, email: resp?.email || email });
      setSubmitting(false);
    } catch (err) {
      // FR4 — backend errors arrive on the OMEGA-590 envelope; err.message is
      // the readable sentence (an already-registered-email 409, a 503). We render it
      // as-is.
      setError(err.message || "Registration failed");
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (!pending || resending || onCooldown) return;
    setResending(true);
    setResendNotice("");
    try {
      await api.resendVerification(pending.identifier);
      // Always 202 (no account enumeration) — one neutral confirmation.
      setResendNotice("Verification email re-sent. Check your inbox.");
      startCooldown();
    } catch (err) {
      setResendNotice(err.message || "Could not resend the verification email.");
    } finally {
      setResending(false);
    }
  };

  // L01 — check-your-inbox state (verification pending). The magic link in the
  // email verifies + auto-logs-in; from here the user only needs their inbox,
  // with a resend affordance if the mail did not arrive.
  if (pending) {
    return (
      <div style={{ padding: 14 }}>
        <div data-testid="register-pending" style={{ fontSize: 13, color: COLORS.text, marginBottom: 10, lineHeight: 1.6 }}>
          Check your inbox. We sent a verification link to{" "}
          <span style={{ color: COLORS.textBr, fontWeight: 700 }}>{pending.email}</span>. Click the
          link to activate your account and sign in.
        </div>
        {resendNotice && (
          <div data-testid="register-resend-notice" style={{ fontSize: 13, color: COLORS.textDim, marginBottom: 8, lineHeight: 1.6 }}>
            {resendNotice}
          </div>
        )}
        <button
          onClick={resend}
          disabled={resending || onCooldown}
          data-testid="register-resend"
          style={{
            width: "100%", padding: 7, borderRadius: 3,
            border: `1px solid ${COLORS.accent}`, background: COLORS.accentDim,
            color: COLORS.accent, fontSize: 13, fontFamily: MONO_FONT,
            fontWeight: 700, cursor: resending ? "wait" : onCooldown ? "not-allowed" : "pointer",
            textTransform: "uppercase", opacity: resending || onCooldown ? 0.6 : 1,
          }}
        >
          {resending
            ? "Sending..."
            : onCooldown
              ? `Resend available in ${remaining}s`
              : "Resend verification email"}
        </button>
        <button
          onClick={onBackToLogin}
          style={{
            width: "100%", padding: 5, marginTop: 6, borderRadius: 3,
            border: `1px solid ${COLORS.border}`, background: "transparent",
            color: COLORS.textDim, fontSize: 13, fontFamily: MONO_FONT,
            cursor: "pointer", textTransform: "uppercase",
          }}
        >
          Back to login
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 14 }}>
      <div style={{ marginBottom: 8 }}>
        {/* F15 FR3 (OMEGA-800) — htmlFor/id on all four fields, so each label NAMES its
            input. Prefixed per screen (see LoginScreen): both auth screens can be mounted
            in one test run, and FR4 requires the six ids to be mutually distinct.

            OMEGA-814 REPLACED this field rather than relabelling it. F15 renamed the
            original "Operator" label to "Username"; a day later registration stopped
            asking for a handle at all and asks for a DISPLAY NAME, because the account's
            `users.username` is now its normalized email — the Email field below. The id is
            therefore `register-full-name`: `register-username` would name a field that no
            longer exists, sitting beside the one that actually becomes the username. */}
        <label htmlFor="register-full-name" style={labelStyle}>Full Name</label>
        <input
          id="register-full-name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          /* OMEGA-814 — "name", not "username": this is the customer's display name and is
             not what they sign in with. The sign-in credential is the Email field below.
             That field carries autoComplete="email" because it is a registration field;
             the login screen's identity input carries "username", which is the sign-in
             value and deliberately not the same token. */
          autoComplete="name"
          maxLength={FULL_NAME_MAX_LENGTH}
          data-testid="register-full-name"
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 8 }}>
        <label htmlFor="register-email" style={labelStyle}>Email</label>
        <input
          id="register-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 8 }}>
        <label htmlFor="register-password" style={labelStyle}>Password</label>
        <input
          id="register-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          style={inputStyle}
        />
        <PasswordRequirements password={password} />
      </div>
      {/* E20.4 — optional (D1). The label says so, because an unmarked empty field on a sign-up
          form reads as a mistake the user is about to be told off for. */}
      <div style={{ marginBottom: 8 }}>
        <label htmlFor="register-legal-name" style={labelStyle}>Legal entity name (optional)</label>
        <input
          id="register-legal-name"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          placeholder="The entity an invoice is made out to"
          autoComplete="organization"
          maxLength={200}
          data-testid="register-legal-name"
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 8 }}>
        <label htmlFor="register-billing-email" style={labelStyle}>Billing email (optional)</label>
        <input
          id="register-billing-email"
          type="email"
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
          placeholder="Defaults to your sign-in email"
          // "billing email", not "email". The HTML autofill grammar allows a `billing`
          // section prefix, and it is the accurate token: this is the address an invoice
          // goes to, not the identity the customer signs in with, so a browser or password
          // manager must not treat the two as one field. It also keeps exactly one input on
          // this screen carrying the bare `email` token, which the sign-in field owns.
          autoComplete="billing email"
          maxLength={320}
          data-testid="register-billing-email"
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 8 }}>
        <label htmlFor="register-confirm-password" style={labelStyle}>Confirm Password</label>
        <input
          id="register-confirm-password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          onKeyDown={(e) => e.key === "Enter" && submit()}
          style={inputStyle}
        />
      </div>
      {error && (
        <div data-testid="register-error" style={{ fontSize: 13, color: COLORS.danger, marginBottom: 6 }}>
          {error}
        </div>
      )}
      <button
        onClick={submit}
        disabled={submitting}
        style={{
          width: "100%", padding: 7, borderRadius: 3,
          border: `1px solid ${COLORS.accent}`, background: COLORS.accentDim,
          color: COLORS.accent, fontSize: 13, fontFamily: MONO_FONT,
          fontWeight: 700, cursor: submitting ? "wait" : "pointer",
          textTransform: "uppercase", opacity: submitting ? 0.6 : 1,
        }}
      >
        {submitting ? "Provisioning..." : "Create Account"}
      </button>
      <button
        onClick={onBackToLogin}
        style={{
          width: "100%", padding: 5, marginTop: 6, borderRadius: 3,
          border: `1px solid ${COLORS.border}`, background: "transparent",
          color: COLORS.textDim, fontSize: 13, fontFamily: MONO_FONT,
          cursor: "pointer", textTransform: "uppercase",
        }}
      >
        Back to login
      </button>
    </div>
  );
}

/**
 * F02 registration screen. Mirrors LoginScreen's panel chrome and theme tokens.
 *
 * OMEGA-762 — no longer wrapped in Stripe ``<Elements>``, and no longer gated on a
 * publishable key. That gate was a real availability cliff, not a formality: with
 * the key absent the whole screen rendered "Registration is unavailable: payment
 * processing is not configured (VITE_STRIPE_PUBLISHABLE_KEY is missing)" and nobody
 * could create an account at all — a customer blocked from signing up, and told so
 * by way of an internal environment-variable name. Registration no longer depends
 * on payment configuration of any kind, so both the gate and its copy are gone.
 */
export default function RegisterScreen({ onRegistered, onBackToLogin }) {
  return (
    <div style={{
      minHeight: "100vh", background: COLORS.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: MONO_FONT,
    }}>
      <div style={{
        width: 460, background: COLORS.panel, border: `1px solid ${COLORS.border}`,
        borderRadius: 6, overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${COLORS.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 12, color: COLORS.textDim, letterSpacing: ".2em", textTransform: "uppercase" }}>
            Omega AI HPC | theomega.ai
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.textBr, marginTop: 2 }}>
            OMEGA AI HPC REGISTRATION
          </div>
        </div>

        <RegisterForm onRegistered={onRegistered} onBackToLogin={onBackToLogin} />
      </div>
    </div>
  );
}
